import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  IconButton,
  Box,
} from '@mui/material';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import IconOnlyChip from './IconOnlyChip';
import { fetchDepartments } from '../services/api';
import { formatPrevSubmission } from '../utils/formatPrevSubmission';

const EditDepartmentDialog = ({ open, onClose, department: deptProp, onResubmit }) => {
  const department = deptProp ?? {};
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [departments, setDepartments] = useState([]);
  const [duplicate, setDuplicate] = useState(false);

  useEffect(() => {
    if (open) {
      setEditing(false);
      setName('');
      setDuplicate(false);
      const instId = deptProp?.institution?.id;
      if (instId) {
        fetchDepartments(instId)
          .then((res) => {
            setDepartments(res || []);
          })
          .catch(() => setDepartments([]));
      } else {
        setDepartments([]);
      }
    }
  }, [open, deptProp]);

  useEffect(() => {
    if (!editing) {
      setDuplicate(false);
      return;
    }
    const lower = name.trim().toLowerCase();
    const dup =
      lower &&
      departments.some(
        (d) => d.id !== department.id && d.name.toLowerCase() === lower
      );
    setDuplicate(dup);
  }, [name, departments, editing, department.id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!deptProp) return;
    const payload = {};
    if (editing) payload.name = name || department.name;
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    if (onResubmit) onResubmit(department.id, payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle>Edit Rejected Department</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Department Name
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>{department.name || '—'}</Typography>
            {editing ? (
              <>
                <TextField
                  size="small"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="New Department Name"
                  error={duplicate}
                  helperText={
                    duplicate ? 'Department with this name already exists.' : ''
                  }
                />
                <IconOnlyChip
                  icon={<CancelIcon fontSize="small" />}
                  onClick={() => {
                    setEditing(false);
                    setName('');
                    setDuplicate(false);
                  }}
                />
              </>
            ) : (
              <IconOnlyChip
                icon={<EditIcon fontSize="small" />}
                onClick={() => setEditing(true)}
              />
            )}
          </Box>
        </Box>
        {department.rejection_reason && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Reason for Rejection
            </Typography>
            <Typography>{department.rejection_reason}</Typography>
          </Box>
        )}
        {department.previous_submission && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Previous Submission
            </Typography>
            <Typography>{formatPrevSubmission(department.previous_submission)}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="info" />
        <FilledButton
          type="submit"
          text="Resubmit"
          color="info"
        />
      </DialogActions>
    </Dialog>
  );
};

export default EditDepartmentDialog;
